import { SimpleGuard, WatchingGuard } from 'interfaces/guards'
import * as get from 'lodash/get'
import { hasSelectedTotalsSelector, isPeergroupsLoadedSelector } from 'selectors/peergroupSelectors'
import { getPeergroupsActionCreator, getPeergroupsTotalsActionCreator } from 'actions/peergroupActions'
import { getMemberInfoActionCreator } from 'actions/memberActions'
import { lastMonthOfTimeframeSelector, memberDataSelector, memberSelector } from 'selectors/globalSelectors'
import { TimeframeTyped } from 'interfaces/global'
import { getFeedMemberInfoActionCreator } from 'actions/feedActions'
import { GetFeatureFlagsAction } from 'actions/globalActions'

type MemberDataKind = 'member' | 'feedMember'
const createMemberAuthGuard = (kind: MemberDataKind): SimpleGuard => {
  const isRegularMember = kind === 'member'
  const actionCreator = isRegularMember
    ? getMemberInfoActionCreator
    : () => getFeedMemberInfoActionCreator.request({})

  const dataPath = `info.${ isRegularMember ? 'id' : 'uid' }`

  return {
    name: isRegularMember ? 'Member Auth' : 'Feed Member Auth',
    checker: (state) => {
      const member = memberDataSelector(state)

      if (member === null) {
        return false
      }

      return member.isAuthenticated ? get(member, dataPath, null) !== null : true
    },
    loader: (dispatch) => dispatch(actionCreator()),
  }
}

export const memberAuthGuard = createMemberAuthGuard('member')
export const feedMemberAuthGuard = createMemberAuthGuard('feedMember')

export const memberInfoGuard: SimpleGuard = {
  name: 'Member Info',
  checker: (state) => {
    const member = memberSelector(state)
    if (member === null) {
      return false
    }

    return !!(member.data?.info?.id ?? null) && !member.isStale
  },
  loader: (dispatch) => dispatch(getMemberInfoActionCreator()),
}

export const feedMemberInfoGuard: SimpleGuard = {
  name: 'Member Feed Info',
  checker: (state) => {
    const member = memberSelector(state)
    if (member === null) {
      return false
    }

    return !!(member.data?.info?.uid) && !member.isStale
  },
  loader: (dispatch) => dispatch(getFeedMemberInfoActionCreator.request({})),
}

export const peergroupsGuard: SimpleGuard = {
  name: 'Peer Groups',
  checker: isPeergroupsLoadedSelector,
  loader: (dispatch) => dispatch(getPeergroupsActionCreator()),
}

export const featureFlagsGuard: SimpleGuard = {
  name: 'Feature Flags',
  checker: (state) => state.data.flags !== null,
  loader: (dispatch) => dispatch(new GetFeatureFlagsAction()),
}

export const selectedPeergroupTotalsGuard: WatchingGuard<TimeframeTyped, { date: string, ids: number[] }> = {
  name: 'Selected Peer Group Totals',
  checker: hasSelectedTotalsSelector,
  watcher: (state, ownProps) => {
    const date = lastMonthOfTimeframeSelector(state, ownProps)
    const allIds = state.data.peergroups.peergroups.allKeys
    const totals = state.data.peergroups.totals[date]
    const ids = totals
      ? allIds.filter((id) => !totals.byKey[id])
      : allIds

    return { date, ids }
  },
  loader: (dispatch, { date, ids }) => dispatch(
    getPeergroupsTotalsActionCreator(date, ids),
  ),
}
